import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route as RouterRoute, Switch } from 'react-router-dom';

import useRoutes from '../../../hooks/useRoutes';
import { ReduxState } from '../../../redux/types';
import Route from '../../../types/Route';

/*
 * This component takes the routes defined in routes.ts,
 * checks if the current user can access the required page, and renders the components.
 *
 * It renders the consents page in the case the user is not an admin and
 * has not yet accepted the privacy policy.
 */

const Router = () => {
    const finalRoutes = useRoutes();

    const userInfo = useSelector((state: ReduxState) => state.user);

    const userRole = useMemo(() => {
        return userInfo.type;
    }, [userInfo]);

    const isLoggedIn = useMemo(() => {
        return userInfo.id > 0;
    }, [userInfo]);

    const getRenderComponent = useCallback((route: Route) => {
        if (!isLoggedIn && route.requireAuth) {
            // if the user is not logged in and the route requires authorization, redirects to login page
            return <Redirect to='/login' />;
        }

        if ((route.roles && !route.roles.includes(userRole)) || (isLoggedIn && !route.canAuth)) {
            // if the user roles is not in the allowed roles for this the route, redirects to the home page
            return <Redirect to='/' />;
        }

        if (route.component) {
            // if the component is the same for all the user roles, return it
            return route.component;
        }

        if (route.components) {
            // if every role has its own component, find and return the right component for the current user's role
            const componentRole = route.components.find(component => {
                return component.role === userRole;
            });

            if (componentRole) {
                return componentRole.component;
            }
        }

        // if no component found, redirect to the home page
        return <Redirect to='/' />;
    }, [isLoggedIn, userRole]);

    const renderRoutes = useMemo(() => {
        return finalRoutes.map((route) => {
            const component = getRenderComponent(route);
            return (
                <RouterRoute
                    path={route.path}
                    exact={route.exactPath}
                    component={() => {
                        return component;
                    }}
                    key={route.path}
                />
            );
        });
    }, [finalRoutes, getRenderComponent]);

    return (
        <Switch>
            {renderRoutes}
        </Switch>
    );
};

export default Router;
